import { useEffect, useCallback, ReactElement } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setUserActivity, setUserIdle } from "src/ui";
import { throttle } from "src/ui/helpers";

const trackedEvents = [
    "mousemove",
    "touchmove",
    "visibilitychange",
] as const;

const idleTimeoutMs
    //= 10000 as const; // 10 seconds
    = 600000 as const; // 10 minutes

/**
 * ActivityRecorder is a React component that tracks user activity and reports
 * it to redux.
 *
 * @returns {ReactElement|null} The rendered component.
 */
function ActivityRecorder(): ReactElement | null {
    const dispatch = useDispatch();
    const lastActivity = useSelector<Spintr.AppState, number>(
        (appState) => appState.ui.lastUserActivity,
    );

    const onActivity = useCallback(
        throttle(function _ActivityRecorderOnActivity() {
            const now = Date.now();

            dispatch(setUserActivity(now));
            dispatch(setUserIdle(false));
        }, 10000, null),
        [dispatch]);

    useEffect(function _ActivityRecorderOnActivityUpdate() {
        const idleTimeout = setTimeout(function _ActivityRecorderOnIdle() {
            dispatch(setUserIdle(true));
        }, idleTimeoutMs);

        return function _ActivityRecorderOnActivityUpdate() {
            clearTimeout(idleTimeout);
        };
    }, [lastActivity, dispatch]);

    useEffect(function _ActivityRecorderMount() {
        trackedEvents.forEach((eventName) => {
            window.addEventListener(eventName, onActivity);
        });

        return function _ActivityRecorderUnmount() {
            trackedEvents.forEach((eventName) => {
                window.removeEventListener(eventName, onActivity);
            });
        };
    }, [onActivity]);

    return null;
}

export default ActivityRecorder;

